import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Bold, Italic, Link, Unlink } from 'lucide-react';

/**
 * Lightweight Rich Text Editor
 * Uses contentEditable + execCommand for bold/italic/link support.
 * Outputs HTML string via onChange callback.
 *
 * Props:
 *  - value: HTML string to display (initial / controlled)
 *  - onChange: (html: string) => void
 *  - placeholder: string
 *  - className: optional extra classes for the editable area
 *  - editorRef: optional forwarded ref to the contentEditable div
 *  - onFocus: optional focus handler
 */
const RichTextEditor = ({ value = '', onChange, placeholder = '', className = '', editorRef: externalRef, onFocus }) => {
    const internalRef = useRef(null);
    const editorRef = externalRef || internalRef;
    const isComposing = useRef(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('https://');
    const savedSelection = useRef(null);

    // Sync value into editor only when value prop changes AND editor is not focused
    // This prevents cursor jumping during typing
    const lastSetHtml = useRef(value);
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        // Only update if the value changed externally (not from typing)
        if (value !== lastSetHtml.current && document.activeElement !== el) {
            el.innerHTML = value || '';
            lastSetHtml.current = value;
        }
    }, [value, editorRef]);

    // Initialize content on mount
    useEffect(() => {
        const el = editorRef.current;
        if (el && !el.innerHTML && value) {
            el.innerHTML = value;
            lastSetHtml.current = value;
        }
    }, []);  // mount-only: seed editor with initial value

    const emitChange = useCallback(() => {
        const el = editorRef.current;
        if (!el) return;
        const html = el.innerHTML || '';
        // Normalize: treat '<br>' or empty divs as empty
        const isEmpty = html === '<br>' || html === '<div><br></div>' || html.trim() === '';
        const normalized = isEmpty ? '' : html;
        lastSetHtml.current = normalized;
        onChange && onChange(normalized);
    }, [onChange, editorRef]);

    const handleInput = useCallback(() => {
        if (!isComposing.current) {
            emitChange();
        }
    }, [emitChange]);

    // Save current selection range (for link dialog)
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            savedSelection.current = sel.getRangeAt(0).cloneRange();
        }
    };

    // Restore saved selection range
    const restoreSelection = () => {
        if (savedSelection.current) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedSelection.current);
        }
    };

    const execFormat = (command, val = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, val);
        emitChange();
    };

    const handleBold = (e) => {
        e.preventDefault();
        execFormat('bold');
    };

    const handleItalic = (e) => {
        e.preventDefault();
        execFormat('italic');
    };

    const handleLinkClick = (e) => {
        e.preventDefault();
        saveSelection();
        setLinkUrl('https://');
        setShowLinkInput(true);
    };

    const handleInsertLink = (e) => {
        e.preventDefault();
        if (!linkUrl || linkUrl === 'https://') {
            setShowLinkInput(false);
            return;
        }
        restoreSelection();
        editorRef.current?.focus();
        document.execCommand('createLink', false, linkUrl);
        // Make links open in new tab: find any <a> without target and add target="_blank"
        const el = editorRef.current;
        if (el) {
            el.querySelectorAll('a:not([target])').forEach(a => {
                a.setAttribute('target', '_blank');
                a.setAttribute('rel', 'noopener noreferrer');
            });
        }
        emitChange();
        setShowLinkInput(false);
        setLinkUrl('https://');
    };

    const handleUnlink = (e) => {
        e.preventDefault();
        execFormat('unlink');
    };

    const handleKeyDown = (e) => {
        // Ctrl/Cmd+B = Bold
        if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
            e.preventDefault();
            execFormat('bold');
        }
        // Ctrl/Cmd+I = Italic
        if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
            e.preventDefault();
            execFormat('italic');
        }
        // Ctrl/Cmd+K = Link
        if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
            e.preventDefault();
            handleLinkClick(e);
        }
    };

    /**
     * Insert raw HTML at the current cursor position (used for variable tokens).
     * Falls back to plain text insertion if insertHTML is unsupported.
     */
    const insertHtmlAtCursor = useCallback((html) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        document.execCommand('insertHTML', false, html);
        emitChange();
    }, [editorRef, emitChange]);

    // Expose insertHtmlAtCursor on the ref so parent can call it
    useEffect(() => {
        const el = editorRef.current;
        if (el) {
            el.insertVariable = insertHtmlAtCursor;
        }
    }, [editorRef, insertHtmlAtCursor]);

    return (
        <div className="border border-notion-border rounded-lg overflow-hidden shadow-sm bg-white">
            {/* Toolbar */}
            <div className="bg-stone-50 border-b border-notion-border p-2 flex items-center gap-1">
                <button
                    type="button"
                    onMouseDown={handleBold}
                    className="p-1.5 rounded hover:bg-stone-200 transition-colors"
                    title="Bold (Ctrl+B)"
                >
                    <Bold className="w-3.5 h-3.5 text-stone-600" />
                </button>
                <button
                    type="button"
                    onMouseDown={handleItalic}
                    className="p-1.5 rounded hover:bg-stone-200 transition-colors"
                    title="Italic (Ctrl+I)"
                >
                    <Italic className="w-3.5 h-3.5 text-stone-600" />
                </button>
                <div className="w-px h-4 bg-stone-200 mx-1" />
                <button
                    type="button"
                    onMouseDown={handleLinkClick}
                    className="p-1.5 rounded hover:bg-stone-200 transition-colors"
                    title="Insert Link (Ctrl+K)"
                >
                    <Link className="w-3.5 h-3.5 text-stone-600" />
                </button>
                <button
                    type="button"
                    onMouseDown={handleUnlink}
                    className="p-1.5 rounded hover:bg-stone-200 transition-colors"
                    title="Remove Link"
                >
                    <Unlink className="w-3.5 h-3.5 text-stone-400" />
                </button>

                {/* Inline link URL input */}
                {showLinkInput && (
                    <div className="flex items-center gap-1 ml-2 bg-white border border-stone-200 rounded px-2 py-0.5">
                        <input
                            type="url"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleInsertLink(e); if (e.key === 'Escape') setShowLinkInput(false); }}
                            placeholder="https://example.com"
                            className="text-xs outline-none w-40 bg-transparent"
                            autoFocus
                        />
                        <button
                            type="button"
                            onMouseDown={handleInsertLink}
                            className="text-[10px] font-bold text-stone-600 hover:text-stone-900 px-1"
                        >
                            OK
                        </button>
                        <button
                            type="button"
                            onMouseDown={() => setShowLinkInput(false)}
                            className="text-[10px] text-stone-400 hover:text-stone-600 px-1"
                        >
                            ✕
                        </button>
                    </div>
                )}
            </div>

            {/* Editable area */}
            <div className="relative">
                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    className={`w-full min-h-[12rem] p-3 text-sm text-stone-700 outline-none overflow-auto rich-text-editable ${className}`}
                    onInput={handleInput}
                    onCompositionStart={() => { isComposing.current = true; }}
                    onCompositionEnd={() => { isComposing.current = false; handleInput(); }}
                    onKeyDown={handleKeyDown}
                    onFocus={onFocus}
                    onBlur={emitChange}
                    data-placeholder={placeholder}
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                />
                {/* CSS-only placeholder via :empty pseudo */}
                <style>{`
          .rich-text-editable:empty:before {
            content: attr(data-placeholder);
            color: #a8a29e;
            pointer-events: none;
            position: absolute;
            top: 0.75rem;
            left: 0.75rem;
          }
          .rich-text-editable a {
            color: #2563eb;
            text-decoration: underline;
          }
          .rich-text-editable b, .rich-text-editable strong {
            font-weight: 700;
          }
          .rich-text-editable i, .rich-text-editable em {
            font-style: italic;
          }
        `}</style>
            </div>
        </div>
    );
};

export default RichTextEditor;
